import React from 'react'
import { Button, Icon, Empty } from 'antd'
import moment from 'moment'
import { STATUS_COLUMNS } from './utils'
import AlertCard from './AlertCard'

function AlertBoard({ alerts, relatedCountsByAlertId = {}, expandedColumn, onToggleColumnExpand, onCardClick, onBulkAction, onBulkIgnore, bulkIgnoreLoading = false, onBulkDirectAction, bulkDirectActionLoading = false, isDark = false }) {
  const columnBackground = isDark ? '#434f5a' : 'rgba(255, 255, 255, 0.95)'
  const columnBorder = isDark ? '#495762' : '#f4f5f7'
  const mutedText = isDark ? '#a1a1a1' : '#6b778c'
  const headerChipBg = isDark ? '#3b4550' : '#f4f5f7'
  const headerChipColor = mutedText
  const columnShadow = isDark ? '0 1px 3px rgba(0, 0, 0, 0.35)' : '0 1px 3px rgba(9, 30, 66, 0.15)'

  const getCardsForColumn = (status) => {
    const filtered = alerts.filter((r) => r.status === status)
    return filtered.sort((a, b) => {
      const dateA = moment(a.createdAt)
      const dateB = moment(b.createdAt)
      return dateB.valueOf() - dateA.valueOf()
    })
  }

  return (
    <div style={{ padding: '12px 20px 20px', overflowX: 'auto', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          gap: 14,
          width: '100%',
          alignItems: 'flex-start',
          flex: 1,
          minHeight: 0
        }}
      >
        {STATUS_COLUMNS.map((column) => {
          const cards = getCardsForColumn(column.id)
          const isExpanded = expandedColumn === column.id
          const hasExpanded = expandedColumn !== null
          // Bulk Direct Action / Bulk Ignore follow the same authority as "Complete
          // Action": only alerts the user may complete (server's `canComplete`).
          const authorizedCount = cards.filter((c) => c && c.canComplete === true).length

          return (
            <div
              key={column.id}
              style={{
                background: columnBackground,
                borderRadius: 10,
                padding: 10,
                width: isExpanded ? '100%' : hasExpanded ? 0 : 320,
                minWidth: isExpanded ? '100%' : hasExpanded ? 0 : 320,
                maxWidth: isExpanded ? 'none' : hasExpanded ? 0 : 320,
                height: isExpanded ? '100%' : 'auto',
                maxHeight: isExpanded ? '100%' : 'calc(100vh - 300px)',
                display: hasExpanded && !isExpanded ? 'none' : 'flex',
                flexDirection: 'column',
                boxShadow: columnShadow,
                flex: isExpanded ? '1 1 100%' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {column.label}
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 999, background: headerChipBg, color: headerChipColor, border: `1px solid ${columnBorder}` }}>
                    {cards.length}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {column.subtitle && <span style={{ fontSize: 11, color: mutedText }}>{column.subtitle}</span>}
                  <Button
                    size="small"
                    onClick={() => onToggleColumnExpand(column.id)}
                    style={{ fontSize: 11, padding: '4px 8px' }}
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    <Icon type={isExpanded ? 'fullscreen-exit' : 'fullscreen'} />
                  </Button>
                </div>
              </div>
              {column.id === 'NEW' && cards.length > 0 && onBulkDirectAction && (
                <Button
                  size="small"
                  type="primary"
                  icon="check-circle"
                  onClick={() => onBulkDirectAction(cards)}
                  loading={bulkDirectActionLoading}
                  disabled={authorizedCount === 0}
                  title={authorizedCount === 0 ? 'None of these alerts are yours to complete (you are not the original assignee or their supervisor)' : undefined}
                  style={{ marginBottom: 8, fontSize: 11 }}
                  block
                >
                  Bulk Direct Action ({authorizedCount})
                </Button>
              )}
              {column.id === 'NEW' && cards.length > 0 && onBulkIgnore && (
                <Button
                  size="small"
                  type="danger"
                  icon="stop"
                  onClick={() => onBulkIgnore(cards)}
                  loading={bulkIgnoreLoading}
                  disabled={authorizedCount === 0}
                  title={authorizedCount === 0 ? 'None of these alerts are yours to complete (you are not the original assignee or their supervisor)' : undefined}
                  style={{ marginBottom: 8, fontSize: 11 }}
                  block
                >
                  Bulk Ignore ({authorizedCount})
                </Button>
              )}
              {(column.id === 'UNDER_ANALYSIS' || column.id === 'ACTION_PENDING') && cards.length > 0 && onBulkAction && (
                <Button
                  size="small"
                  icon="thunderbolt"
                  onClick={() => onBulkAction(column.id, cards)}
                  style={{ marginBottom: 8, fontSize: 11 }}
                  block
                >
                  {column.id === 'UNDER_ANALYSIS' ? 'Bulk Analysis' : 'Bulk Action Taken'} ({cards.length})
                </Button>
              )}
              <div
                style={{
                  overflowY: 'auto',
                  paddingRight: 4,
                  display: 'flex',
                  flexDirection: isExpanded ? 'row' : 'column',
                  gap: 8,
                  minHeight: 40,
                  flexWrap: isExpanded ? 'wrap' : 'nowrap',
                  flex: 1,
                  height: isExpanded ? '100%' : 'auto'
                }}
              >
                {cards.length > 0 ? (
                  cards.map((card) => (
                    <div key={card.id} style={{ width: isExpanded ? 260 : '100%', flexShrink: 0 }}>
                      <AlertCard
                        alert={card}
                        relatedCount={relatedCountsByAlertId[String(card._id || card.id || '')] || 0}
                        onClick={onCardClick}
                        isDark={isDark}
                      />
                    </div>
                  ))
                ) : (
                  <div style={{ padding: 20, textAlign: 'center', color: mutedText, fontSize: 12 }}>
                    No alerts
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default React.memo(AlertBoard)
